import type { ILanguageJson } from '../types/languageJson';
import type { ILocaleTextMap } from '../types/localeText';
import { getRuntimeLocale, normalizeLocaleTextMap, resolveLocaleText } from './localeText';

type LocaleSource = string | ILocaleTextMap | null | undefined;

export const LANGUAGE_JSON_KEYS = {
  prefix: 'prefix',
  navigator: 'navigator',
  h5ShareTitle: 'h5_share_title',
  h5ShareText: 'h5_share_text',
} as const;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !(value instanceof Array);
};

/**
 * `language_json` 是后端新增的统一多语言入口：
 * - galleryconf.saveone 负责全局层（prefix / h5_share_title / h5_share_text）
 * - gallerypgdata.savemore 负责页面层（navigator / h5_share_title / h5_share_text）
 *
 * 这里统一负责解析字符串 / 对象两种形态，避免调用方到处写 JSON.parse 与空值判断。
 */
export const normalizeLanguageJson = (
  value?: ILanguageJson | string | null
): ILanguageJson => {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value);
      return isPlainObject(parsedValue) ? (parsedValue as ILanguageJson) : {};
    } catch (error) {
      return {};
    }
  }

  return isPlainObject(value) ? value : {};
};

/**
 * 返回一个map ,形如 { 'zh-CN': 'xxx', 'en-US': 'xxx' }
 * @param languageJson
 * @param key 心如: 'navigator' / 'h5_share_title' / 'h5_share_text' / 'prefix'
 */
export const getLanguageJsonField = (
  languageJson: ILanguageJson | string | null | undefined,
  key: string
): ILocaleTextMap | undefined => {
  return normalizeLanguageJson(languageJson)[key];
};

/**
 * 统一的新旧兼容规则：
 * 1. 优先读取 `language_json[key]`
 * 2. 再回退到历史的 `*_i18n`
 * 3. 最后回退到旧的单字符串字段
 */
export const resolveLanguageJsonText = (
  legacyValue?: LocaleSource,
  languageJson?: ILanguageJson | string | null,
  key: string = '',
  locale: string = getRuntimeLocale(),
  legacyLocaleValue?: LocaleSource
): string => {
  const localeValue = key
    ? getLanguageJsonField(languageJson, key) || legacyLocaleValue
    : legacyLocaleValue;

  return resolveLocaleText(legacyValue, localeValue, locale);
};
